import { ModalDialog, PrimaryButton, TextButton } from '@smartbear/swagger-portal-component-library'
import { clsx } from 'clsx'
import { Trans, useTranslation } from 'react-i18next'
import { useMediaQuery } from 'usehooks-ts'
import { z } from 'zod'

import { useSubmitting } from '~/portal/hooks/useSubmitting'
import appDefaults from '~/portal/portal.defaults.json'
import { ACTION_TYPE } from '~/portal/services/actions'
import { LandingProduct } from '~/portal/types/product'
import Form from '~/shared/components/Form'

const CREATE_REQUEST_ACCESS_ACTION = `/?action=${ACTION_TYPE.CREATE_REQUEST_ACCESS}`

export interface RequestAccessModalProps {
  modalOpen: boolean;
  setModalOpen: (value: boolean) => void;
  product: LandingProduct;
  userFullName: string;
  accentColor?: string;
}

export const RequestAccessModal = ({
  modalOpen,
  setModalOpen,
  product,
  userFullName,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept for backward-compatible prop signature; confirm button border is now driven solely by --sp-color-accent
  accentColor,
}: RequestAccessModalProps) => {
  const { t } = useTranslation()

  const isDesktop = useMediaQuery(appDefaults.media.desktop, { initializeWithValue: false, defaultValue: true })

  const submitting = useSubmitting(CREATE_REQUEST_ACCESS_ACTION, () => {
    setModalOpen(false)
  })
  
  const schema = z.object({ note: z.string().max(appDefaults.requestAccessModal.notes.maxLength).default('') })

  return (
    <ModalDialog
      transition={isDesktop}
      closeOnOverlayClick={isDesktop}
      loading={submitting}
      isOpen={modalOpen}
      onClose={() => setModalOpen(false)}
      size="m"
      translations={{
        title: t('portal.landingPage.requestAccessModal.requestAccess.title'),
        description: <Trans t={t} i18nKey="portal.landingPage.requestAccessModal.requestAccess.subtitle" values={{ activeProduct: product.name }} />
      }}
    >
      <Form schema={schema} method="post" mode="onChange" action={CREATE_REQUEST_ACCESS_ACTION} >
        {({ Field, watch }) => {
          const watchNote = watch('note')

          return (
            <section className="flex flex-col gap-4">
              <Field
                name="note"
                placeholder={t('portal.landingPage.requestAccessModal.requestAccess.notes.placeholder')}
              >
                {({ Input, Label }) =>
                  <>
                    <Label className="font-semibold text-(--sp-color-text-muted)">
                      {t('portal.landingPage.requestAccessModal.requestAccess.notes.label')}
                    </Label>
                    <Input maxLength={appDefaults.requestAccessModal.notes.maxLength} />
                    <div className="flex justify-end mt-1! text-body3 text-(--sp-color-text-muted)">
                      <p>{t('portal.landingPage.requestAccessModal.requestAccess.notes.counter.label')}</p>
                      <p className="ml-1 font-semibold">{watchNote.length} / {appDefaults.requestAccessModal.notes.maxLength}</p>
                    </div>
                  </>
                }
              </Field>
              <input type="hidden" name="fullName" value={userFullName} />
              <input type="hidden" name="productId" value={product.id} />
              <div className="flex justify-end gap-2">
                <TextButton
                  type="button"
                  onClick={() => setModalOpen(false)}
                  size="m"
                  className="hover:bg-(--sp-color-surface-inverse) dark:hover:bg-(--sp-color-surface-inverse) hover:text-(--sp-color-text-inverse) dark:hover:text-(--sp-color-text-inverse)"
                >
                  {t('components.ui.buttons.cancel.label')}
                </TextButton>
                <PrimaryButton
                  data-testid="consumer-request-access-confirm"
                  loading={submitting}
                  size="m"
                  type="submit"
                  className={clsx(
                    !submitting && 'border ml-2 duration-200 transition-color bg-transparent text-(--sp-color-text) dark:text-(--sp-color-text) border-(--sp-color-accent) dark:border-(--sp-color-accent)',
                    !submitting && 'hover:bg-(--sp-color-surface-inverse) dark:hover:bg-(--sp-color-surface-inverse) hover:border-(--sp-color-surface-inverse) hover:text-(--sp-color-text-inverse) dark:hover:text-(--sp-color-text-inverse)',
                    !submitting && 'dark:bg-transparent',
                  )}
                >{t('components.ui.buttons.confirm.label')}</PrimaryButton>
              </div>
            </section>
          )
        }}
      </Form>
    </ModalDialog>
  )
}